import { useState, useEffect } from 'react';

import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';
import { BreedOptionType, formatBreedOptions, filterSearchValues } from '@modernanimal/common/src/utils/patientOnboarding';
import { useBreakpoint } from 'use-breakpoint';

import { getBreeds, getColors } from './patientOnboarding.helpers';
import {
    CustomerBreedSpeciesEnum, CustomerColorSpeciesEnum, CustomerPatientSexEnum, GetColorsQuery
} from '../__generated__/graphql';
import PrimaryButton from '../components/PrimaryButton';
import SearchInputField from '../components/SearchInputField';
import {
    BREED_NOT_PROVIDED_CAT, BREED_NOT_PROVIDED_DOG, COLOR_NOT_PROVIDED_CAT, COLOR_NOT_PROVIDED_DOG
} from '../constants';
import Fonts from '../fonts';
import { BREAKPOINTS } from '../helpers';
import useWebBookingStore from '../store';

type Props = {
    goToNextStep: ()=> void;
};

const PatientAttributes = ({ goToNextStep }: Props) => {
    const { patient, updatePatient } = useWebBookingStore((state) => ({
        patient: state.patient,
        updatePatient: state.setPatient,
    }));
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    const [breed, setBreed] = useState<BreedOptionType>(patient.breed);
    const [breedOptions, setBreedOptions] = useState<BreedOptionType[]>([]);
    const [isBreedValid, setIsBreedValid] = useState(true);
    const [otherBreedSearchResult, setOtherBreedSearchResult] = useState<BreedOptionType | undefined>(undefined);
    const [color, setColor] = useState<GetColorsQuery['colors'][0]>(patient.color);
    const [colorOptions, setColorOptions] = useState<GetColorsQuery['colors']>([]);
    const [isColorValid, setIsColorValid] = useState(true);
    const [sex, setSex] = useState<CustomerPatientSexEnum | 'Unknown' | null>(
        // If patient.sex is null, we can determine if it's Unknown or not selected by checking
        // if the breed has been filled out already. If it has, we know the user has selected Unknown.
        patient.sex ?? (patient.breed.id ? 'Unknown' : null)
    );
    const [spayedOrNeutered, setSpayedOrNeutered] = useState<boolean | null>(patient.spayedOrNeutered);

    const isValidInfo = !!breed.id && !!color.id && sex !== null;

    useEffect(() => {
        const fetchBreedsAndColors = async () => {
            if (!patient.species) return;

            const breeds = await getBreeds(patient.species as unknown as CustomerBreedSpeciesEnum);
            const formattedBreedOptions = formatBreedOptions(breeds);
            setBreedOptions(formattedBreedOptions);
            const otherBreed = formattedBreedOptions.find((breedOption) => breedOption.name === 'Other');
            if (otherBreed) setOtherBreedSearchResult(otherBreed);

            const colors = await getColors(patient.species as unknown as CustomerColorSpeciesEnum);
            setColorOptions(colors);
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchBreedsAndColors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleNext = () => {
        updatePatient({
            ...patient,
            breed,
            color,
            sex: sex === 'Unknown' ? null : sex,
            spayedOrNeutered
        });
        goToNextStep();
    };

    const selectSex = (patientSex: CustomerPatientSexEnum | 'Unknown') => {
        setSex(sex !== patientSex ? patientSex : null);
        setSpayedOrNeutered(null);
    };

    const selectSpayedOrNeutered = (isSpayedOrNeutered: boolean) => setSpayedOrNeutered(
        spayedOrNeutered === isSpayedOrNeutered ? null : isSpayedOrNeutered
    );

    const renderBreedInput = () => (
        <Subbody>
            <Fonts.LabelMedium>Breed</Fonts.LabelMedium>
            <SearchInputField
                placeholder="Choose breed"
                selectedValue={breed}
                searchQuery={(searchValue) => {
                    const breedResults = searchValue !== '' ? filterSearchValues(searchValue, breedOptions, 15) : [];
                    // this temporarily hides values that should not be shown
                    const formattedBreedResults = breedResults
                        .filter((breedResult) => breedResult.id !== BREED_NOT_PROVIDED_DOG
                            && breedResult.id !== BREED_NOT_PROVIDED_CAT)
                        .map((breedResult) => ({
                            ...breedResult,
                            isMixed: !!breedResult.isMixed
                        }));
                    return formattedBreedResults;
                }}
                onSelectResult={(result) => setBreed({
                    id: result.id,
                    name: result.name,
                    isMixed: !!result.isMixed
                })}
                searchResultKey={(result) => `${result.name}${result.isMixed ? '-isMixed' : ''}`}
                searchResultName="name"
                autoFocus={!breed}
                otherSearchResult={otherBreedSearchResult}
                isSearchValueValid={isBreedValid}
                setIsSearchValueValid={setIsBreedValid}
            />
            <Fonts.Body style={{ color: colors.GREY_2 }}>
                {'If you don\'t know the breed, type "other."'}
            </Fonts.Body>
            {!isBreedValid && (
                <Fonts.Body style={{ color: colors.RED }}>
                    Please select from the dropdown.
                </Fonts.Body>
            )}
        </Subbody>
    );

    const renderColorInput = () => (
        <Subbody>
            <Fonts.LabelMedium>Color</Fonts.LabelMedium>
            <SearchInputField
                placeholder="Predominant color"
                selectedValue={color}
                searchQuery={(searchValue) => {
                    const colorResults = searchValue !== '' ? filterSearchValues(searchValue, colorOptions, 15) : [];
                    // this temporarily hides values that should not be shown
                    const filteredColorResults = colorResults.filter(
                        (colorResult) => colorResult.id !== COLOR_NOT_PROVIDED_DOG
                            && colorResult.id !== COLOR_NOT_PROVIDED_CAT
                    );
                    return filteredColorResults;
                }}
                onSelectResult={(result) => setColor({
                    id: result.id,
                    name: result.name
                })}
                searchResultKey="name"
                searchResultName="name"
                isSearchValueValid={isColorValid}
                setIsSearchValueValid={setIsColorValid}
            />
            {!isColorValid && (
                <Fonts.Body style={{ color: colors.RED }}>
                    Please select from the dropdown.
                </Fonts.Body>
            )}
        </Subbody>
    );

    const renderSexSelections = () => {
        const sexSelectionOptions: (CustomerPatientSexEnum | 'Unknown')[] = [CustomerPatientSexEnum.M, CustomerPatientSexEnum.F, 'Unknown'];
        const showSpayedOrNeutered = sex === CustomerPatientSexEnum.F || sex === CustomerPatientSexEnum.M;
        return (
            <>
                <SexSelectionRow>
                    {sexSelectionOptions.map((patientSex) => {
                        const isSelected = sex === patientSex;
                        const SexSelectionFont = isSelected ? Fonts.LabelMedium : Fonts.Body;
                        let sexSelectionText = 'Unknown';
                        if (patientSex === CustomerPatientSexEnum.M) sexSelectionText = 'Male';
                        else if (patientSex === CustomerPatientSexEnum.F) sexSelectionText = 'Female';

                        return (
                            <SexSelection
                                key={`patient-sex-${sexSelectionText}`}
                                isSelected={isSelected}
                                onClick={() => selectSex(patientSex)}
                            >
                                <SexSelectionFont style={{ color: isSelected ? colors.GREY_1 : colors.GREY_3 }}>
                                    {sexSelectionText}
                                </SexSelectionFont>
                            </SexSelection>

                        );
                    })}
                </SexSelectionRow>
                {showSpayedOrNeutered && (
                    <SexSelectionRow>
                        {[true, false].map((isSpayedOrNeutered) => {
                            const spayedOrNeuteredText = sex === CustomerPatientSexEnum.F ? 'Spayed' : 'Neutered';
                            const isSelected = spayedOrNeutered === isSpayedOrNeutered;
                            const SpayedOrNeuteredFont = isSelected ? Fonts.LabelMedium : Fonts.Body;
                            return (
                                <SexSelection
                                    key={`${isSpayedOrNeutered ? '' : 'not-'}spayed-or-neutered`}
                                    isSelected={isSelected}
                                    onClick={() => selectSpayedOrNeutered(isSpayedOrNeutered)}
                                >
                                    <SpayedOrNeuteredFont style={{ color: isSelected ? colors.GREY_1 : colors.GREY_3 }}>
                                        {isSpayedOrNeutered ? spayedOrNeuteredText : `Not ${spayedOrNeuteredText}`}
                                    </SpayedOrNeuteredFont>
                                </SexSelection>
                            );
                        })}
                    </SexSelectionRow>
                )}
            </>
        );
    };

    return (
        <PatientAttributesContainer>
            <Body>
                <Fonts.Heading04>{`Tell us more about ${patient.name}`}</Fonts.Heading04>
                {renderBreedInput()}
                {renderColorInput()}
                <Subbody>
                    <Fonts.LabelMedium>Sex</Fonts.LabelMedium>
                    {renderSexSelections()}
                </Subbody>
            </Body>
            <NextButtonContainer isMobile={isMobile}>
                <PrimaryButton id="patients-attributes-button" disabled={!isValidInfo} onClick={handleNext} color="periwinkle">
                    Next
                </PrimaryButton>
            </NextButtonContainer>
        </PatientAttributesContainer>
    );
};

export default PatientAttributes;

const PatientAttributesContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 32px 24px 0 24px;
    gap: 8px;
    justify-content: space-between;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const NextButtonContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    bottom: 0;
    background-color: ${colors.WHITE};
    padding: 16px 0 32px 0;
`;

const SexSelection = styled.button<{ isSelected: boolean }>`
    display: flex;
    height: 48px;
    justify-content: center;
    align-items: center;
    flex: 1;
    border-radius: 40px;
    background-color: ${({ isSelected }) => (isSelected ? colors.WHITE : colors.GREY_6)};
    border: ${({ isSelected }) => `1px solid ${isSelected ? colors.PERIWINKLE_4 : colors.GREY_6}`};
`;

const SexSelectionRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
`;

const Subbody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
